import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../environments/environment';
import {
    ApiRequestSnippet,
    RequestSnippet,
    requestSnippetApiToLocal,
    RequestSnippetLang,
    requestSnippetLangs,
    SnippetLang
} from '../helpers/request-snippet';
import { buildCurlSnippet, buildNodejsSnippet, buildPythonSnippet } from '../helpers/request-snippet-builders';

import { SnippetService } from './snippet.service';

@Injectable({
    providedIn: 'root'
})
export class RequestSnippetService {

    private lang: RequestSnippetLang;

    private langChangeSource: Subject<void> = new Subject<void>();
    private langChange: Observable<void> = this.langChangeSource.asObservable();

    constructor(
        private snippetService: SnippetService,
    ) {
        this.initLang();
    }

    /**
     * Build HTML markup for the snippet provided.
     * @param snippet Request snippet object.
     */
    buildSnippet(snippet: RequestSnippet): string {
        switch (this.lang.name) {
        case SnippetLang.curl:
            return buildCurlSnippet(snippet);
        case SnippetLang.nodejs:
            return buildNodejsSnippet(snippet);
        case SnippetLang.python:
            return buildPythonSnippet(snippet);
        }
    }

    /**
     * Programming language selected.
     */
    getLang(): RequestSnippetLang {
        return this.lang;
    }

    /**
     * Programming language change event.
     */
    getLangChange(): Observable<void> {
        return this.langChange;
    }

    /**
     * Initializes service.
     */
    private initLang(): void {
        const langName: string = localStorage.getItem(environment.snippetLangStorage);
        const lang: RequestSnippetLang = requestSnippetLangs.find((item: RequestSnippetLang): boolean => {
            return item.name === langName;
        });

        if (lang) {
            this.lang = lang;
        } else {
            this.lang = requestSnippetLangs[0];
            localStorage.setItem(environment.snippetLangStorage, this.lang.name);
        }
    }

    /**
     * Deserializes request snippet.
     * @param html Request snippet string.
     */
    parseSnippet(html: string): RequestSnippet {
        const noPrefix: string = html.replace(environment.requestSnippetPrefix, '');
        const json: string = this.snippetService.cleanup(noPrefix);
        let apiSnippet: ApiRequestSnippet;

        try {
            apiSnippet = JSON.parse(json);
        } catch (error) {
            throw new Error('Snippet parse error');
        }

        try {
            return requestSnippetApiToLocal(apiSnippet);
        } catch (error) {
            throw new Error('Snippet has invalid format');
        }
    }

    /**
     * Sets programming language for request snippets.
     * @param lang Programming language setting.
     */
    setLang(lang: RequestSnippetLang): void {
        this.lang = lang;
        localStorage.setItem(environment.snippetLangStorage, this.lang.name);

        this.langChangeSource.next();
    }
}
